/**
 * Мини-система тостов (уведомления «изменено в другом месте», ошибки сохранения).
 * Без внешних либ: локальный хук useToasts + компонент ToastHost. Автоскрытие по таймеру.
 */

import { useCallback, useRef, useState } from 'react';
import { cx } from './cx';

export type ToastTone = 'info' | 'success' | 'error';

export interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

export interface ToastsApi {
  toasts: Toast[];
  push: (message: string, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
}

const DEFAULT_TTL_MS = 4000;

export function useToasts(): ToastsApi {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone = 'info') => {
      const id = nextId.current++;
      setToasts((list) => [...list, { id, tone, message }]);
      window.setTimeout(() => dismiss(id), DEFAULT_TTL_MS);
    },
    [dismiss],
  );

  return { toasts, push, dismiss };
}

export function ToastHost({ toasts, dismiss }: { toasts: Toast[]; dismiss: (id: number) => void }): JSX.Element {
  return (
    <div className="pw-toasts" role="region" aria-label="Уведомления" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={cx('pw-toast', `pw-toast--${t.tone}`)} role="status">
          <span className="pw-toast__msg">{t.message}</span>
          <button type="button" className="pw-toast__close" aria-label="Закрыть уведомление" onClick={() => dismiss(t.id)}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
